/**
 * Report Service
 *
 * Client-side service for report generation API calls.
 */

import type {
  ReportTemplate,
  ReportGenerationRequest,
  ReportGenerationResult,
} from 'shared';
import { apiService } from './api.service';

const BASE_PATH = '/reports';

// Get API URL - use VITE_API_URL in production, relative path in development
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

/**
 * Get all available report templates
 */
export async function getTemplates(): Promise<ReportTemplate[]> {
  return apiService.get<ReportTemplate[]>(`${BASE_PATH}/templates`);
}

/**
 * Get a specific report template by ID
 */
export async function getTemplate(id: string): Promise<ReportTemplate> {
  return apiService.get<ReportTemplate>(`${BASE_PATH}/templates/${id}`);
}

/**
 * Generate a report and get the result with base64 PDF
 */
export async function generateReport(
  request: ReportGenerationRequest
): Promise<ReportGenerationResult> {
  return apiService.post<ReportGenerationResult>(
    `${BASE_PATH}/generate`,
    request
  );
}

/**
 * Generate and download a report as PDF file
 */
export async function downloadReport(
  request: ReportGenerationRequest
): Promise<void> {
  // Use API_BASE_URL for production, relative URL for dev
  const response = await globalThis.fetch(
    `${API_BASE_URL}${BASE_PATH}/generate/download`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Only add ngrok header for local ngrok development
        ...(window.location.hostname.includes('ngrok') && {
          'ngrok-skip-browser-warning': 'true',
        }),
      },
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to generate report');
  }

  // Get filename from header or generate one
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = 'report.pdf';
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="(.+)"/);
    if (match) {
      filename = match[1];
    }
  }

  // Download the file
  const blob = await response.blob();
  const url = globalThis.URL.createObjectURL(blob);
  const link = globalThis.document.createElement('a');
  link.href = url;
  link.download = filename;
  globalThis.document.body.appendChild(link);
  link.click();
  globalThis.document.body.removeChild(link);
  globalThis.URL.revokeObjectURL(url);
}

/**
 * Clear AI cache for a specific scope
 */
export async function clearCache(scopeId: string): Promise<void> {
  await apiService.delete(`${BASE_PATH}/cache/${scopeId}`);
}

/**
 * Clear all AI cache
 */
export async function clearAllCache(): Promise<void> {
  await apiService.delete(`${BASE_PATH}/cache`);
}
